import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { useGetRolesQuery, useGetUsersQuery, useUpdateDistributorCompanyMutation } from 'api/main';
import { userSchemas } from 'schemas';
import { useMatchAtLeastOneRole } from 'utils/hooks';
import { parseDate, parseFullName, purgeEmptyFields } from 'utils/helpers';
import { positions } from 'utils/formData';
import { RoleNames } from 'utils/constants';
import { texts } from 'utils/languages/fr';

import InfoRow from './InfoRow';
import InfoBox from './InfoBox';
import SectionActions from './SectionActions';
import { CustomPhoneField, CustomSelect, CustomTextField, Loader } from 'components';
import { IconButton, MenuItem } from '@mui/material';
import * as icons from 'mdi-material-ui';

const getDefaultForm = () => ({
    representativeName: '',
    representativeRole: '',
    function: '',
});

const RepresentativeSection = props => {
    const { distributor, onSendMessage, canEditMissingInfo } = props;
    const [form, setForm] = useState(getDefaultForm());
    const [editing, setEditing] = useState(false);

    const { data: roles = [], isLoading: rolesLoader } = useGetRolesQuery();
    const { data: { userList: adminUsers = [] } = { userList: [] }, isLoading: usersLoading } =
        useGetUsersQuery(
            userSchemas.getUsers({
                companyId: distributor.id,
                role: [
                    roles
                        .filter(r => r.name === RoleNames.ADMIN_CIF || r.name === RoleNames.PSI)
                        .map(r => r.id),
                ],
            }),
            { skip: !roles.length }
        );
    const [updateDistributorCompany, { isLoading: loading }] =
        useUpdateDistributorCompanyMutation();

    const userIsAdminOrSignatoryServicer = useMatchAtLeastOneRole([
        RoleNames.SERVICER_ADMIN,
        RoleNames.SERVICER_SIGNATORY,
    ]);

    const userFullName = useMemo(() => parseFullName(distributor?.user), [distributor]);

    const handleEdit = useCallback(() => {
        setForm({
            representativeName: distributor?.legalRepresentant
                ? userFullName
                : distributor?.representativeName,
            representativeRole: distributor?.representativeRole,
            userId: distributor?.user?.id,
            function: distributor?.function,
            phoneNumber: distributor?.user?.phoneNumber,
            email: distributor?.user?.email,
        });
        setEditing(true);
    }, [distributor, userFullName]);

    const handleChange = useCallback(
        key => e =>
            setForm(prevState => ({
                ...prevState,
                email:
                    key === 'userId'
                        ? adminUsers.find(u => u.id === +e.target.value)?.email
                        : prevState.email,
                [key]: e.target.value,
            })),
        [adminUsers]
    );
    const handlePhoneChange = useCallback(phone => {
        setForm(oldValue => ({ ...oldValue, phoneNumber: phone }));
    }, []);
    const handleCancel = useCallback(() => {
        setForm(getDefaultForm());
        setEditing(false);
    }, []);
    const handleSubmit = useCallback(() => {
        const { userId, ...purgedForm } = purgeEmptyFields(form);

        if (Object.keys(purgedForm).length) {
            const payload = {
                id: distributor.id,
                legalRepresentant: false,
                ...purgedForm,
                user: userId ? `/api/users/${userId}` : undefined,
            };

            updateDistributorCompany({
                payload,
                callback: () => {
                    setForm(getDefaultForm());
                    setEditing(false);
                },
            });
        }
    }, [form, distributor, updateDistributorCompany]);

    const hasMissingInfo =
        (distributor?.legalRepresentant === true
            ? false
            : !distributor?.representativeName || !distributor?.representativeRole) ||
        !distributor?.function;

    return (
        <InfoBox
            title={`${texts.REPRESENTANT_LEGAL} / ${texts.SIGNER}`}
            titleAction={
                canEditMissingInfo && (
                    <IconButton onClick={editing ? null : handleEdit} disableRipple>
                        <icons.Pencil
                            sx={{ color: 'primary.main', '&:hover': { color: 'primary.dark' } }}
                        />
                    </IconButton>
                )
            }
        >
            <InfoRow
                title={texts.LEGAL_REPRESENTATIVE_LAST_NAME}
                data={
                    distributor?.legalRepresentant === true
                        ? userFullName
                        : distributor?.representativeName || '-'
                }
                editing={editing}
            >
                {(canEditMissingInfo || editing) && (
                    <CustomTextField
                        value={form.representativeName}
                        onChange={handleChange('representativeName')}
                    />
                )}
            </InfoRow>
            <InfoRow
                title={texts.LEGAL_REPRESENTATIVE_POSITION}
                data={distributor?.representativeRole || '-'}
                editing={editing}
            >
                {canEditMissingInfo && !editing && (
                    <CustomTextField
                        value={form.representativeRole}
                        onChange={handleChange('representativeRole')}
                    />
                )}
                {editing && (
                    <CustomSelect
                        value={form.representativeRole}
                        onChange={handleChange('representativeRole')}
                    >
                        {positions.map(opt => (
                            <MenuItem key={opt} value={opt}>
                                {opt}
                            </MenuItem>
                        ))}
                    </CustomSelect>
                )}
            </InfoRow>
            <InfoRow title={texts.SIGNER_NAME} data={userFullName} editing={editing}>
                {(canEditMissingInfo || editing) && (
                    <Loader loading={usersLoading || rolesLoader} marginTop={0}>
                        <CustomSelect value={form.userId} onChange={handleChange('userId')}>
                            {adminUsers.map(user => (
                                <MenuItem key={user.id} value={user.id}>
                                    {parseFullName(user)}
                                </MenuItem>
                            ))}
                        </CustomSelect>
                    </Loader>
                )}
            </InfoRow>
            <InfoRow
                title={texts.SIGNER_POSITION}
                data={distributor?.function || '-'}
                editing={editing}
            >
                {(canEditMissingInfo || editing) && (
                    <CustomSelect value={form.function} onChange={handleChange('function')}>
                        {positions.map(opt => (
                            <MenuItem key={opt} value={opt}>
                                {opt}
                            </MenuItem>
                        ))}
                    </CustomSelect>
                )}
            </InfoRow>
            <InfoRow
                editing={editing}
                title={texts.E_MAIL}
                data={distributor?.user.email || '-'}
                clickable={userIsAdminOrSignatoryServicer}
                onClick={userIsAdminOrSignatoryServicer ? onSendMessage : null}
            >
                {editing && <CustomTextField disabled value={form.email} />}
            </InfoRow>
            <InfoRow
                title={texts.TELEPHONE}
                data={distributor?.user.phoneNumber || '-'}
                editing={editing}
            >
                {editing && (
                    <CustomPhoneField
                        value={form.phoneNumber}
                        onChange={handlePhoneChange}
                        hideInfoButton
                        sx={{ width: '100%' }}
                    />
                )}
            </InfoRow>
            <InfoRow
                title={texts.DERNIERE_CONNEXION}
                data={parseDate(distributor?.user.lastLoggedAt) || '-'}
            />
            {((hasMissingInfo && canEditMissingInfo) || editing) && (
                <SectionActions
                    loading={loading}
                    handleCancel={handleCancel}
                    handleSubmit={handleSubmit}
                />
            )}
        </InfoBox>
    );
};

RepresentativeSection.propTypes = {
    distributor: PropTypes.object,
    onSendMessage: PropTypes.func,
    canEditMissingInfo: PropTypes.bool,
};

export default RepresentativeSection;
